import "firebase/auth";
import "firebase/storage";
import "firebase/firestore";
import cryptService from "./crypt.service";
import AuthService from "./auth.service";
import { expandRoles } from "@/utils/roles";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  DocumentData,
  WhereFilterOp,
  orderBy,
  OrderByDirection,
  onSnapshot,
  addDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  limit,
  startAfter,
  runTransaction,
} from "firebase/firestore";

import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
  UploadMetadata,
  getMetadata,
  deleteObject,
  listAll
} from "firebase/storage";

export default class FirebaseService {
  static refFromUrl(url: string) {
    const fileRef = storageRef(AuthService.storage, url);

    return fileRef;
  }

  static async uploadStorageFile(
    file: any,
    path: string,
    returnUrl = true,
    metadata?: UploadMetadata,
  ): Promise<any> {
    const fileRef = storageRef(AuthService.storage, path);
    const snapshot = await uploadBytes(fileRef, file, metadata);

    if (returnUrl) {
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    }

    return null;
  }

  static async getDownloadURLStorageFile(path: string): Promise<any> {
    const fileRef = storageRef(AuthService.storage, path);

    const downloadURL = await getDownloadURL(fileRef);
    return downloadURL;

  }

  static async downloadFileFromStorage(path: string, fileName?: string, returnBlob = false): Promise<Blob | void> {
    try {
      const fileRef = storageRef(AuthService.storage, path);
      const downloadURL = await getDownloadURL(fileRef);

      // Extract filename from path if not provided
      const finalFileName = fileName || path.split('/').pop() || 'download';

      // Fetch the file as a blob to force download
      const response = await fetch(downloadURL);
      const blob = await response.blob();

      if (returnBlob) {
        return blob;
      }

      // Create object URL from blob
      const blobUrl = URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = finalFileName;

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL
      URL.revokeObjectURL(blobUrl);

    } catch (error) {
      throw error;
    }
  }

  static async fileExistsInStorage(path: string): Promise<boolean> {
    try {
      const fileRef = storageRef(AuthService.storage, path);
      await getMetadata(fileRef); // Attempt to fetch metadata
      console.log(`File at ${path} exists.`);
      return true; // If metadata fetch is successful, file exists
    } catch (error: any) {
      if (error.code === "storage/object-not-found") {
        console.log(`File at ${path} does not exist.`);
        return false; // If file not found, return false
      }
      console.error("Error checking file existence:", error);
      throw error; // Re-throw unexpected errors
    }
  }

  static async fileExistsInFolder(folderPath: string, fileName: string): Promise<boolean> {
    const files = await this.listFilesInFolder(folderPath);
    return files.includes(fileName); // Check if the file exists in the list
  }


  static async deleteFileFromStorage(path: string): Promise<void> {
    try {
      const fileRef = storageRef(AuthService.storage, path);
      await deleteObject(fileRef);
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  }

  static async deleteFolder(folderPath: string): Promise<void> {
    try {
      const fileRef = storageRef(AuthService.storage, folderPath);
      const listResult = await listAll(fileRef);

      const deletionPromises = listResult.items.map(item =>
        deleteObject(item)
      );

      await Promise.all(deletionPromises);

      const folderDeletionPromises = listResult.prefixes.map(prefix =>
        this.deleteFolder(prefix.fullPath)
      );

      await Promise.all(folderDeletionPromises);
    } catch (error) {
      console.error(`Error deleting folder ${folderPath}:`, error);
    }
  }

  static async listFilesInFolder(folderPath: string): Promise<string[]> {
    const fileRef = storageRef(AuthService.storage, folderPath);
    const files = await listAll(fileRef);
    const items: string[] = files.items.map((item: any) => item.name); return items;
  }


  static async getUserData(): Promise<any> {
    const authService = new AuthService();
    const user = await authService.getCurrentUser();
    if (!user) {
      return;
    }
    const token: any = await user.getIdTokenResult();
    const attributes: any = cryptService.decrypt(token.claims.attributes, true);
    const dbUser = attributes.user.dbUser;
    if (!dbUser.user_attr) {
      dbUser.user_attr = { roles: [] };
    }
    dbUser.user_attr.roles = expandRoles(dbUser.user_attr.roles);
    return { ...dbUser, id: attributes.user.dbUser.user_id };
  }

  static async getDefaultDoc(company_id: string | null | undefined = null): Promise<any> {
    const authService = new AuthService();
    const user = await authService.getCurrentUser();
    if (!user) {
      throw new Error("FB User not authenticated");
    }
    const token: any = await user.getIdTokenResult();
    const attributes: any = cryptService.decrypt(token.claims.attributes, true);
    const companyId = company_id || attributes.user.company_id;
    return doc(AuthService.db, `tenants/${companyId}`);
  }

  static async getDocData(path: string, company_id: string | null | undefined = null): Promise<DocumentData | undefined> {
    const defaultDoc = await FirebaseService.getDefaultDoc(company_id);
    const ref = doc(defaultDoc, path);
    const docSnap = await getDoc(ref);
    return docSnap.data();
  }

  static async getCollectionDocs(
    path: string,
    extractData: boolean,
    wheres: { fieldPath: string; opStr: WhereFilterOp; value: any }[] = [],
    orderByFields?: [string, OrderByDirection][],
    limitCount?: number,
    startAfterValue?: DocumentData,
    company_id: string | null | undefined = null
  ): Promise<DocumentData[]> {
    const defaultDoc = await FirebaseService.getDefaultDoc(company_id);

    console.log('defaultDoc', defaultDoc);
    console.log('path', path);
    console.log('wheres', wheres);
    console.log('orderByFields', orderByFields);
    console.log('limitCount', limitCount);
    console.log('startAfterValue', startAfterValue);
    console.log('company_id', company_id);

    let q = query(collection(defaultDoc, path));

    // Apply where filters if provided
    wheres.forEach(({ fieldPath, opStr, value }) => {
      q = query(q, where(fieldPath, opStr, value));
    });

    // Apply orderBy if provided
    if (orderByFields) {
      orderByFields.forEach(([field, direction]) => {
        q = query(q, orderBy(field, direction));
      });
    }

    // Apply limit if provided
    if (typeof limitCount === "number") {
      q = query(q, limit(limitCount));
    }

    // Apply startAfter if provided
    if (startAfterValue) {
      q = query(q, startAfter(startAfterValue));
    }

    const querySnapshot = await getDocs(q);
    if (extractData) {
      return querySnapshot.docs.map((doc) => doc.data());
    }
    return querySnapshot.docs;
  }

  static async docSnapshotListener(
    path: string,
    callback: (data: DocumentData | undefined) => void,
    company_id: string | null | undefined = null,
    errorCallback?: (error: Error) => void,
  ): Promise<() => void> {
    const defaultDoc = await this.getDefaultDoc(company_id);
    const docRef = doc(defaultDoc.firestore, `${defaultDoc.path}/${path}`);
    const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
      callback(docSnapshot.exists() ? docSnapshot.data() : undefined);
    }, errorCallback);
    return unsubscribe;
  }

  static async collectionSnapshotListener(
    path: string,
    extractData: boolean,
    callback: (docs: DocumentData[]) => void,
    wheres: {
      fieldPath: string;
      opStr: WhereFilterOp;
      value: any;
    }[] = [],
    orderByFields?: [string, OrderByDirection][],
    limitCount?: number,
    startAfterValue?: DocumentData,
    company_id: string | null | undefined = null,
    errorCallback?: (error: Error) => void,
  ): Promise<() => void> {
    const defaultDoc = await this.getDefaultDoc(company_id);
    let q = query(
      collection(defaultDoc.firestore, `${defaultDoc.path}/${path}`),
    );

    // Apply where filters if provided
    wheres.forEach(({ fieldPath, opStr, value }) => {
      q = query(q, where(fieldPath, opStr, value));
    });

    // Apply orderBy if provided
    if (orderByFields) {
      orderByFields.forEach(([field, direction]) => {
        q = query(q, orderBy(field, direction));
      });
    }

    // Apply limit if provided
    if (typeof limitCount === "number") {
      q = query(q, limit(limitCount));
    }

    // Apply startAfter if provided
    if (startAfterValue) {
      q = query(q, startAfter(startAfterValue));
    }

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        if (extractData) {
          const docs = querySnapshot.docs.map((doc) => doc.data());
          callback(docs);
        } else {
          callback(querySnapshot.docs);
        }
      },
      (error) => errorCallback?.(error),
    );
    return unsubscribe;
  }

  static async addDocument(
    path: string,
    data: DocumentData,
    addDocId = true,
    company_id: string | null | undefined = null
  ): Promise<void> {
    const defaultDoc = await FirebaseService.getDefaultDoc(company_id);
    const collectionRef = collection(defaultDoc, path);
    const docRef = await addDoc(collectionRef, data);

    if (addDocId) {
      await setDoc(docRef, { ...data, id: docRef.id }, { merge: true });
    }
  }

  static async setDocument(
    path: string,
    data: DocumentData,
    merge = false,
    company_id: string | null | undefined = null
  ): Promise<void> {
    const defaultDoc = await FirebaseService.getDefaultDoc(company_id);
    const docRef = doc(defaultDoc, path);
    await setDoc(docRef, data, { merge });
  }

  static async updateDocument(
    path: string,
    data: DocumentData,
    company_id: string | null | undefined = null
  ): Promise<void> {
    const defaultDoc = await FirebaseService.getDefaultDoc(company_id);
    const docRef = doc(defaultDoc, path);
    await updateDoc(docRef, data);
  }

  static async advanceMapTimestamp(
    path: string,
    mapField: string,
    mapKey: string,
    value: Date,
    company_id: string | null | undefined = null,
  ): Promise<void> {
    const defaultDoc = await FirebaseService.getDefaultDoc(company_id);
    const docRef = doc(defaultDoc, path);

    await runTransaction(defaultDoc.firestore, async (transaction) => {
      const snapshot = await transaction.get(docRef);
      const data = snapshot.exists() ? snapshot.data() : {};
      const currentMap = { ...(data?.[mapField] || {}) };
      const currentValue = currentMap[mapKey];
      const currentDate = currentValue?.toDate?.() ??
        (currentValue ? new Date(currentValue) : null);

      if (currentDate && currentDate.getTime() >= value.getTime()) {
        return;
      }

      currentMap[mapKey] = value;
      transaction.set(docRef, { [mapField]: currentMap }, { merge: true });
    });
  }

  static async deleteDocument(path: string): Promise<void> {
    const defaultDoc = await FirebaseService.getDefaultDoc();
    const docRef = doc(defaultDoc, path);
    await deleteDoc(docRef);
  }
}
